import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { Release } from '../types';
import { ErrorNote, Field, Modal } from './ui';

interface EditReleaseModalProps {
  release: Release;
  onClose: () => void;
  /** `notify` is true when the person asked to e-mail members about the change. */
  onSuccess: (notify: boolean) => void;
}

const EditReleaseModal: React.FC<EditReleaseModalProps> = ({ release, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [version, setVersion] = useState(release.version);
  const [title, setTitle] = useState(release.title);
  const [description, setDescription] = useState(release.description);
  const [notify, setNotify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await projectService.updateRelease(release.id, {
        version: version.trim(),
        title: title.trim(),
        description: description.trim(),
      });
      toast.success(t('releases.saved'));
      onSuccess(notify);
    } catch (err) {
      console.error('Error updating release:', err);
      setError(t('releases.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('releases.edit')}
      onClose={onClose}
      busy={loading}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="edit-release"
            disabled={loading || !version.trim() || !title.trim()}
            className="btn btn-primary"
          >
            {loading ? t('common.working') : t('common.saveChanges')}
          </button>
        </>
      }
    >
      <form id="edit-release" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[160px_1fr]">
          <Field label={t('releases.version')} htmlFor="edit-version">
            <input id="edit-version" className="input font-mono" value={version} onChange={(e) => setVersion(e.target.value)} required />
          </Field>
          <Field label={t('releases.title')} htmlFor="edit-title">
            <input id="edit-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
        </div>
        <Field label={t('releases.notes')} htmlFor="edit-notes">
          <textarea id="edit-notes" className="input" rows={6} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {t('releases.notifyOnEdit')}
        </label>
        {error && <ErrorNote>{error}</ErrorNote>}
      </form>
    </Modal>
  );
};

export default EditReleaseModal;
